import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {CreateFeedbackRequestDto, FeedbackCategoryTypes} from '../../services/rest/api-types/CreateFeedbackRequestDto';
import {NewFeedbackData} from './NewFeedbackData';

export type FeedbackSteps = 'creation' | 'result' | 'mainTopic' | 'topic';

@Injectable({
    providedIn: 'root'
})

export class FeedbackPopupService {
    private currentStepSubject = new BehaviorSubject<FeedbackSteps | undefined>(undefined);
    public currentStep$: Observable<FeedbackSteps | undefined> = this.currentStepSubject.asObservable();

    private notAllowSubject = new BehaviorSubject<boolean>(false);
    public notAllow$: Observable<boolean> = this.notAllowSubject.asObservable();

    private hasErrorSubject = new BehaviorSubject<boolean>(false);
    public hasError$: Observable<boolean> = this.hasErrorSubject.asObservable();

    private openInSubject = new BehaviorSubject<'question' | 'learningPackage' | undefined>(undefined);
    public openIn$: Observable<'question' | 'learningPackage' | undefined> = this.openInSubject.asObservable();

    public feedbackLevel: 'question' | 'learningPackage' | undefined;

    private newFeedbackData = new BehaviorSubject<NewFeedbackData | undefined>(undefined);
    public newFeedbackData$ = this.newFeedbackData.asObservable();

    public spinnerVisibleSubject = new BehaviorSubject<boolean>(false);
    public spinnerVisible$: Observable<boolean> = this.spinnerVisibleSubject.asObservable();

    public showSpinner(): void {
        this.spinnerVisibleSubject.next(true);
    }

    public hideSpinner(): void {
        this.spinnerVisibleSubject.next(false);
    }

    public get currentStep(): FeedbackSteps | undefined {
        return this.currentStepSubject.getValue();
    }

    public get openIn(): 'question' | 'learningPackage' | undefined {
        return this.openInSubject.getValue();
    }

    public setOpenIn(openIn: 'question' | 'learningPackage' | undefined): void {
        this.openInSubject.next(openIn);
    }

    public setHasError(status: boolean): void {
        this.hasErrorSubject.next(status);
    }

    public setNotAllow(status: boolean): void {
        this.notAllowSubject.next(status);
    }

    public clearPopupStates(): void {
        this.hasErrorSubject.next(false);
        this.notAllowSubject.next(false);
        this.feedbackLevel = undefined;
        this.openInSubject.next(undefined);
        this.currentStepSubject.next(undefined);
        this.newFeedbackData.next(undefined);
    }

    public setQuestionFeedbackLevel(): void {
        this.feedbackLevel = 'question';
    }

    public setLearningPackageFeedbackLevel(): void {
        this.feedbackLevel = 'learningPackage';
    }

    public getTopicPopup(): FeedbackSteps {
        if (this.currentStep === 'creation' || this.currentStep === 'result') {
            return this.currentStep;
        }
        return this.getTopic();
    }

    private getTopic(): FeedbackSteps {
        return this.feedbackLevel === 'question' ? 'mainTopic' : 'topic';
    }

    public setFeedbackIds(learningPackageTranslationId: string, questionId?: string): void {
        let data = this.newFeedbackData.getValue();
        if (data === undefined) {
            const newTopicPopup = this.getTopicPopup();
            data = new NewFeedbackData(newTopicPopup, learningPackageTranslationId, questionId);
            this.currentStepSubject.next(newTopicPopup);
        }
        this.newFeedbackData.next(data)
    }

    private get currentFeedbackData(): NewFeedbackData {
        return this.newFeedbackData.getValue()!;
    }

    public resetFeedback(): void {
        const data = this.currentFeedbackData;
        data.feedbackText = undefined;
        data.feedbackCategory = undefined;
        const topic = this.getTopic();
        data.currentStep = topic;
        this.currentStepSubject.next(topic);
        this.newFeedbackData.next(data);
    }

    public setFeedbackCategory(category: FeedbackCategoryTypes): void {
        const data = this.currentFeedbackData;
        data.feedbackCategory = category;
        this.newFeedbackData.next(data)
    }

    public setFeedbackCurrentStep(currentStep: FeedbackSteps): void {
        this.currentStepSubject.next(currentStep);
        const data = this.currentFeedbackData;
        data.currentStep = currentStep;
        this.newFeedbackData.next(data)
    }
    public setFeedbackResultStep(): void {
        const data = this.currentFeedbackData;
        data.currentStep = 'result';
        data.feedbackText = undefined;
        this.currentStepSubject.next('result');
        this.newFeedbackData.next(data)
    }

    public setFeedbackText(text: string): void {
        const data = this.currentFeedbackData;
        data.feedbackText = text;
        this.newFeedbackData.next(data)
    }

    public setDraft(draft: NewFeedbackData): void {
        if (draft.currentStep !== undefined) {
            this.currentStepSubject.next(draft.currentStep);
        }
        this.newFeedbackData.next({...draft});
    }

    public getCreateFeedbackRequestDto(): CreateFeedbackRequestDto {
        const newFeedbackData = this.currentFeedbackData;
        return new CreateFeedbackRequestDto(
            (newFeedbackData.feedbackText ?? '').trim(),
            newFeedbackData.feedbackCategory!,
            newFeedbackData.learningPackageTranslationId,
            newFeedbackData.quizTranslationId
        );
    }

    public getInitialText(): string {
        const newFeedbackData = this.currentFeedbackData;
        return (newFeedbackData.feedbackText ?? '').trim();
    }

}
